<template>
  <div class="chart-wrap">
    <svg :viewBox="`0 0 ${w} ${viewBoxHeight}`" preserveAspectRatio="none">
      <!-- 그라데이션 정의 -->
      <defs>
        <linearGradient id="hrGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" :stop-color="zoneColors[5]" />
          <stop offset="25%" :stop-color="zoneColors[4]" />
          <stop offset="50%" :stop-color="zoneColors[3]" />
          <stop offset="75%" :stop-color="zoneColors[2]" />
          <stop offset="100%" :stop-color="zoneColors[1]" />
        </linearGradient>
      </defs>

      <!-- Zone 배경 (투명하게 설정) -->
      <g class="zones">
        <rect
          v-for="(zone, index) in zoneRects"
          :key="`zone-${index}`"
          :x="0"
          :y="zone.y"
          :width="w"
          :height="zone.height"
          :fill="zone.color"
          :opacity="0"
        />
      </g>

      <!-- 격자 라인 (가로) - interval 간격으로 동적 생성 -->
      <g class="grid-horizontal">
        <line
          v-for="(line, index) in horizontalGridLines"
          :key="`h-${index}`"
          :x1="0"
          :x2="w"
          :y1="line.y"
          :y2="line.y"
          stroke="#333"
          stroke-dasharray="8,4"
        />
      </g>

      <!-- 격자 라인 (세로) - 10분 간격 -->
      <g class="grid-vertical">
        <line
          v-for="(label, index) in timeLabels"
          :key="`v-${index}`"
          :x1="label.x"
          :x2="label.x"
          :y1="0"
          :y2="h"
          stroke="#333"
          stroke-dasharray="8,4"
        />
      </g>

      <!-- 심박수 라인 (그라데이션) -->
      <g class="hr-line">
        <path
          :d="hrPath"
          fill="none"
          stroke="url(#hrGradient)"
          stroke-width="3"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </g>

      <!-- 시간 라벨 (X축) - 수직선 아래 -->
      <g class="time-labels desktop-only">
        <text
          v-for="(label, index) in timeLabels"
          :key="`time-label-${index}`"
          :x="label.x"
          :y="h + 25"
          text-anchor="middle"
          dominant-baseline="middle"
          class="time-label-text"
        >
          {{ label.value }}
        </text>
      </g>
    </svg>
  </div>
</template>

<script>
export default {
  name: "HrLineChart",
  props: {
    data: { type: Array, required: true }, // [{x:seconds, y:bpm}, ...]
    w: { type: Number, default: 700 },
    h: { type: Number, default: 200 },
    yMin: { type: Number, default: 60 },
    yMax: { type: Number, default: 200 },
    xMax: { type: Number, default: 3600 },
    zoneBoundaries: { type: Object, default: () => ({}) }, // Zone 경계값
  },
  computed: {
    // 모바일 여부 체크
    isMobile() {
      return window.innerWidth <= 768;
    },

    // 동적 viewBox 높이 (그래프 아래 공간 제거)
    viewBoxHeight() {
      return this.h;
    },

    // Zone 색상 정의
    zoneColors() {
      return {
        1: "#49DE80", // Zone1 - 연두색
        2: "#24D3EE", // Zone2 - 하늘색
        3: "#F59E0C", // Zone3 - 주황색
        4: "#EF4444", // Zone4 - 빨간색
        5: "#8B5CF6", // Zone5 - 보라색
        6: "#8B5CF6", // Zone5 초과 - Zone5와 같은 색
      };
    },

    // 동적 Y축 범위 계산
    dynamicYRange() {
      if (
        !this.zoneBoundaries ||
        Object.keys(this.zoneBoundaries).length === 0
      ) {
        return { min: this.yMin, max: this.yMax };
      }

      const boundaries = this.zoneBoundaries;

      // zone1_max와 zone2_max가 없으면 기본값 사용
      if (!boundaries.zone1_max || !boundaries.zone2_max) {
        return { min: this.yMin, max: this.yMax };
      }

      const interval = boundaries.zone2_max - boundaries.zone1_max;
      const min = boundaries.zone1_max - interval;
      const max = boundaries.zone5_max || boundaries.zone1_max + interval * 4;

      return { min, max };
    },

    // interval 간격으로 수평선 생성
    horizontalGridLines() {
      const { min, max } = this.dynamicYRange;
      const interval =
        this.zoneBoundaries?.zone2_max && this.zoneBoundaries?.zone1_max
          ? this.zoneBoundaries.zone2_max - this.zoneBoundaries.zone1_max
          : (max - min) / 4;

      const lines = [];
      let currentY = min;

      while (currentY <= max) {
        lines.push({
          y: this.sy(currentY),
          value: Math.round(currentY),
        });
        currentY += interval;
      }

      return lines;
    },

    // Zone 배경 영역 계산 (수정됨 - SVG는 위가 0이므로)
    zoneRects() {
      if (
        !this.zoneBoundaries ||
        Object.keys(this.zoneBoundaries).length === 0
      ) {
        return [];
      }

      const zones = [];
      const boundaries = this.zoneBoundaries;

      // Zone5 초과 영역 (최상단)
      if (boundaries.zone5_max && boundaries.zone5_max < this.yMax) {
        zones.push({
          y: 0,
          height: this.sy(boundaries.zone5_max),
          color: this.zoneColors[6],
        });
      }

      // Zone5 (zone4_max ~ zone5_max)
      if (boundaries.zone4_max && boundaries.zone5_max) {
        zones.push({
          y: this.sy(boundaries.zone5_max),
          height: this.sy(boundaries.zone4_max) - this.sy(boundaries.zone5_max),
          color: this.zoneColors[5],
        });
      }

      // Zone4 (zone3_max ~ zone4_max)
      if (boundaries.zone3_max && boundaries.zone4_max) {
        zones.push({
          y: this.sy(boundaries.zone4_max),
          height: this.sy(boundaries.zone3_max) - this.sy(boundaries.zone4_max),
          color: this.zoneColors[4],
        });
      }

      // Zone3 (zone2_max ~ zone3_max)
      if (boundaries.zone2_max && boundaries.zone3_max) {
        zones.push({
          y: this.sy(boundaries.zone3_max),
          height: this.sy(boundaries.zone2_max) - this.sy(boundaries.zone3_max),
          color: this.zoneColors[3],
        });
      }

      // Zone2 (zone1_max ~ zone2_max)
      if (boundaries.zone1_max && boundaries.zone2_max) {
        zones.push({
          y: this.sy(boundaries.zone2_max),
          height: this.sy(boundaries.zone1_max) - this.sy(boundaries.zone2_max),
          color: this.zoneColors[2],
        });
      }

      // Zone1 (0 ~ zone1_max) - 최하단
      if (boundaries.zone1_max) {
        zones.push({
          y: this.sy(boundaries.zone1_max),
          height: this.h - this.sy(boundaries.zone1_max),
          color: this.zoneColors[1],
        });
      }

      return zones;
    },

    // Zone별 심박수 라인 세그먼트
    hrSegments() {
      if (!this.data || this.data.length === 0) return [];

      const sorted = [...this.data].sort((a, b) => a.time_min - b.time_min);
      const segments = [];
      let currentSegment = null;
      let currentZone = null;

      for (let i = 0; i < sorted.length; i++) {
        const point = sorted[i];
        const zone = this.getZoneForHR(point.hr_bpm);

        if (currentZone !== zone) {
          // Zone이 바뀌면 이전 세그먼트를 저장하고 새 세그먼트 시작
          if (currentSegment) {
            segments.push(currentSegment);
          }

          currentSegment = {
            path: `M${this.sx(point.time_min * 60)},${this.sy(point.hr_bpm)}`,
            color: this.zoneColors[zone],
            zone: zone,
          };
          currentZone = zone;
        } else {
          // 같은 Zone이면 경로에 점 추가
          if (currentSegment) {
            currentSegment.path += ` L${this.sx(point.time_min * 60)},${this.sy(
              point.hr_bpm
            )}`;
          }
        }
      }

      // 마지막 세그먼트 추가
      if (currentSegment) {
        segments.push(currentSegment);
      }

      return segments;
    },

    // 전체 심박수 라인 경로 (그라데이션용)
    hrPath() {
      if (!this.data || this.data.length === 0) return "";

      const sorted = [...this.data].sort((a, b) => a.time_min - b.time_min);
      let path = "";

      for (let i = 0; i < sorted.length; i++) {
        const point = sorted[i];
        const x = this.sx(point.time_min * 60);
        const y = this.sy(point.hr_bpm);

        if (i === 0) {
          path = `M${x},${y}`;
        } else {
          path += ` L${x},${y}`;
        }
      }

      return path;
    },

    // 실제 데이터 범위 계산
    dataRange() {
      if (!this.data || this.data.length === 0) {
        return { minX: 0, maxX: this.xMax };
      }

      const sorted = [...this.data].sort((a, b) => a.time_min - b.time_min);
      const minX = sorted[0].time_min * 60; // 첫 번째 데이터의 시간 (초)
      const maxX = sorted[sorted.length - 1].time_min * 60; // 마지막 데이터의 시간 (초)

      return { minX, maxX };
    },

    // 5분 간격으로 수직선과 시간 라벨 생성
    timeLabels() {
      const labels = [];
      const intervalMinutes = 5; // 5분 간격
      const intervalSeconds = intervalMinutes * 60; // 300초
      const { minX, maxX } = this.dataRange;

      // 실제 데이터 범위에서 5분 간격으로 생성
      const startTime = Math.floor(minX / intervalSeconds) * intervalSeconds; // 5분 단위로 내림

      for (
        let timeInSeconds = startTime;
        timeInSeconds <= maxX;
        timeInSeconds += intervalSeconds
      ) {
        const minutes = Math.floor(timeInSeconds / 60);
        const seconds = timeInSeconds % 60;
        const x = this.sx(timeInSeconds);

        labels.push({
          x: x,
          value: `${minutes}:${seconds.toString().padStart(2, "0")}`,
        });
      }

      return labels;
    },

    // Zone 경계값 라벨
    zoneLabels() {
      if (
        !this.zoneBoundaries ||
        Object.keys(this.zoneBoundaries).length === 0
      ) {
        return [];
      }

      const labels = [];
      const boundaries = this.zoneBoundaries;

      // Zone5 최대값
      if (boundaries.zone5_max) {
        labels.push({
          value: boundaries.zone5_max,
          y: Math.max(10, this.sy(boundaries.zone5_max)), // 최소 10px 여백 확보
        });
      }

      // Zone4 최대값
      if (boundaries.zone4_max) {
        labels.push({
          value: boundaries.zone4_max,
          y: this.sy(boundaries.zone4_max),
        });
      }

      // Zone3 최대값
      if (boundaries.zone3_max) {
        labels.push({
          value: boundaries.zone3_max,
          y: this.sy(boundaries.zone3_max),
        });
      }

      // Zone2 최대값
      if (boundaries.zone2_max) {
        labels.push({
          value: boundaries.zone2_max,
          y: this.sy(boundaries.zone2_max),
        });
      }

      // Zone1 최대값
      if (boundaries.zone1_max) {
        labels.push({
          value: boundaries.zone1_max,
          y: Math.min(this.h - 10, this.sy(boundaries.zone1_max)), // 최대 h-10px 여백 확보
        });
      }

      return labels;
    },

    // 최대/최소 심박수 포인트 계산
    maxHrPoint() {
      if (!this.data || this.data.length === 0) return null;

      const sorted = [...this.data].sort((a, b) => a.time_min - b.time_min);
      let maxHr = sorted[0];

      for (let i = 1; i < sorted.length; i++) {
        if (sorted[i].hr_bpm > maxHr.hr_bpm) {
          maxHr = sorted[i];
        }
      }

      return {
        x: this.sx(maxHr.time_min * 60),
        y: this.sy(maxHr.hr_bpm),
        value: Math.round(maxHr.hr_bpm),
      };
    },
  },
  methods: {
    // X 좌표 변환 (실제 데이터 범위 기준, 여백 없이 전체 너비 사용)
    sx(x) {
      const { minX, maxX } = this.dataRange;
      const dataWidth = maxX - minX;
      if (dataWidth === 0) return 0; // 데이터가 하나뿐인 경우

      // 여백 없이 전체 너비 사용
      return ((x - minX) / dataWidth) * this.w;
    },

    // Y 좌표 변환 (SVG는 위가 0이므로 반전)
    sy(y) {
      const { min, max } = this.dynamicYRange;
      return this.h - ((y - min) / (max - min)) * this.h;
    },

    // 심박수에 따른 Zone 계산
    getZoneForHR(hr) {
      if (
        !this.zoneBoundaries ||
        Object.keys(this.zoneBoundaries).length === 0
      ) {
        return 1; // 기본값
      }

      const boundaries = this.zoneBoundaries;

      if (hr <= boundaries.zone1_max) return 1;
      if (hr <= boundaries.zone2_max) return 2;
      if (hr <= boundaries.zone3_max) return 3;
      if (hr <= boundaries.zone4_max) return 4;
      if (hr <= boundaries.zone5_max) return 5;
      return 6; // Zone5 초과
    },
  },
};
</script>

<style scoped>
.chart-wrap {
  width: 100%;
  height: 240px;
  background: #0a0a0a;
  border-radius: 5px;
  overflow: hidden;
}

svg {
  width: 100%;
  height: 100%;
  display: block;
}

.zone-label-text {
  fill: #8e8e8e;
  font-size: 12px;
  font-family: "Pretendard", sans-serif;
}

.time-label-text {
  fill: #8e8e8e;
  font-size: 12px;
  font-family: "Pretendard", sans-serif;
}

.hr-extreme-text {
  fill: #fff;
  font-size: 12px;
  font-family: "Pretendard", sans-serif;
  font-weight: 500;
}

/* 모바일 최적화 */
@media (max-width: 768px) {
  .chart-wrap {
    height: 140px;
  }

  .desktop-only {
    display: none;
  }
}
</style>
